import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Snackbar from '@mui/material/Snackbar';
import useMediaQuery from '@mui/material/useMediaQuery';

import Rail from './appShell/Rail';
import TopBar from './appShell/TopBar';
import CommandPalette from './appShell/CommandPalette';
import Icon from './appShell/Icon';
import useActiveNav from './appShell/useActiveNav';
import { HUB_BREADCRUMB_EVENT, readBreadcrumbs } from './appShell/breadcrumbBridge';
import {
  C, FONT, SHADOW, RAIL_WIDTH, RAIL_WIDTH_COLLAPSED,
  DESKTOP_QUERY, WIDE_QUERY,
} from './appShell/tokens';

const LS_EXPANDED = 'hub_rail_expanded';
const SS_TOAST = 'hub_region_toast';

const leafShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  target: PropTypes.string,
});

const propTypes = {
  rootURL: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      url: PropTypes.string,
      chip: PropTypes.string,
      dividerAfter: PropTypes.bool,
      leaves: PropTypes.arrayOf(leafShape),
    })),
  })),
  logoutLink: PropTypes.shape({
    name: PropTypes.string, url: PropTypes.string, method: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.shape({
    name: PropTypes.string, role: PropTypes.string, initials: PropTypes.string,
  }).isRequired,
  currentPath: PropTypes.string.isRequired,
  currentFullPath: PropTypes.string,
  currentRegion: PropTypes.oneOf(['cr', 'gt']).isRequired,
};

const defaultProps = { sections: [], currentFullPath: null };

const AppShell = (props) => {
  const { sections, currentPath, currentFullPath, currentRegion } = props;

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const isWide = useMediaQuery(WIDE_QUERY);
  const isMobile = !isDesktop;

  const active = useActiveNav(sections, currentPath, currentFullPath);

  // `expanded` persists across page loads; 992–1199 defaults to collapsed.
  const [expanded, setExpanded] = useState(() => {
    const stored = typeof window !== 'undefined' && window.localStorage.getItem(LS_EXPANDED);
    if (stored === '1') return true;
    if (stored === '0') return false;
    return isWide;
  });
  const [openGroupId, setOpenGroupId] = useState(active.groupId);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [toast, setToast] = useState(null);
  // Server-provided breadcrumbs published by the current page (shared/AppPage).
  const [pageCrumbs, setPageCrumbs] = useState(() => readBreadcrumbs());

  useEffect(() => {
    const onCrumbs = (e) => setPageCrumbs(e.detail && e.detail.length ? e.detail : null);
    window.addEventListener(HUB_BREADCRUMB_EVENT, onCrumbs);
    return () => window.removeEventListener(HUB_BREADCRUMB_EVENT, onCrumbs);
  }, []);

  // Width the server-rendered <main> should offset by (see .hub-main in hub_shell.scss).
  const railWidth = isMobile ? 0 : (expanded ? RAIL_WIDTH : RAIL_WIDTH_COLLAPSED);
  // Layout effect (pre-paint) keeps the server-rendered <main> offset in sync
  // without a one-frame flash.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--hub-rail-w', `${railWidth}px`);
  }, [railWidth]);

  // Region-switch confirmation survives the full-page navigation via sessionStorage.
  useEffect(() => {
    const pending = window.sessionStorage.getItem(SS_TOAST);
    if (pending) {
      setToast(pending);
      window.sessionStorage.removeItem(SS_TOAST);
    }
  }, []);

  const openPalette = useCallback(() => {
    setPaletteOpen(true);
    setMobileOpen(false);
  }, []);

  // ⌘K / Ctrl+K opens the palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openPalette();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPalette]);

  const toggleRail = () => {
    setExpanded((prev) => {
      const next = !prev;
      window.localStorage.setItem(LS_EXPANDED, next ? '1' : '0');
      return next;
    });
  };

  const toggleGroup = (id) => setOpenGroupId((prev) => (prev === id ? null : id));

  const replaceRegionInUrl = (region) =>
    currentPath.replace(/^\/(cr|gt)(\/|$)/, `/${region}$2`);

  const pickRegion = (region) => {
    const names = { cr: 'Costa Rica', gt: 'Guatemala' };
    // Read on the next page load (the anchor performs the actual navigation).
    window.sessionStorage.setItem(SS_TOAST, `Contexto cambiado a ${names[region]} · /${region}/…`);
  };

  // Flattened, searchable module list for ⌘K.
  const modules = useMemo(() => {
    const out = [];
    sections.forEach((section) => {
      section.groups.forEach((group) => {
        if (!group.leaves || group.leaves.length === 0) {
          out.push({
            name: group.name,
            group: section.label || group.chip || 'General',
            icon: group.icon,
            url: group.url,
          });
        } else {
          group.leaves.forEach((leaf) => {
            out.push({ name: leaf.name, group: group.name, icon: group.icon, url: leaf.url });
          });
        }
      });
    });
    return out;
  }, [sections]);

  const railContent = (
    <Rail
      sections={sections}
      expanded={isMobile ? true : expanded}
      touch={isMobile}
      active={active}
      openGroupId={openGroupId}
      onToggleGroup={toggleGroup}
      onToggleRail={toggleRail}
      currentUser={props.currentUser}
      logoutLink={props.logoutLink}
      rootURL={props.rootURL}
    />
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: RAIL_WIDTH, border: 0, bgcolor: C.ink, overflow: 'hidden' } }}
        >
          {/* Close the temporary drawer after any navigation click. */}
          <Box sx={{ height: '100%' }} onClick={(e) => {
            if (e.target.closest && e.target.closest('a')) setMobileOpen(false);
          }}>
            {railContent}
          </Box>
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: railWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: railWidth, border: 0, bgcolor: C.ink, overflow: 'hidden',
              transition: 'width 200ms cubic-bezier(.2,0,0,1)',
            },
          }}
        >
          {railContent}
        </Drawer>
      )}

      <TopBar
        expanded={expanded}
        isMobile={isMobile}
        railWidth={railWidth}
        active={active}
        onToggleRail={toggleRail}
        onOpenMobile={() => setMobileOpen(true)}
        onOpenPalette={openPalette}
        pageCrumbs={pageCrumbs}
        currentRegion={currentRegion}
        replaceRegionInUrl={replaceRegionInUrl}
        onPickRegion={pickRegion}
      />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} modules={modules} />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{
          bgcolor: C.ink, color: '#fff', p: '12px 18px', borderRadius: '999px', boxShadow: SHADOW.toast,
          font: `600 12px/1 ${FONT}`, display: 'flex', alignItems: 'center', gap: '9px',
        }}>
          <Icon name="repeat" size={15} color={C.turquoise} />
          {toast}
        </Box>
      </Snackbar>
    </>
  );
};

AppShell.propTypes = propTypes;
AppShell.defaultProps = defaultProps;

export default AppShell;
